/**
 * NotificationManager
 * 
 * A utility for displaying consistent notifications throughout the application
 * Supports success, error, warning, and info notification types
 */

class NotificationManager {
  /**
   * Show a notification to the user
   * @param {Object} options - Notification options
   * @param {string} options.type - Type of notification: 'success', 'error', 'warning', 'info'
   * @param {string} options.title - Title of the notification
   * @param {string} options.message - Message content
   * @param {number} options.duration - Duration in ms (default varies by type)
   * @returns {HTMLElement} The created notification element
   */
  static show({ type = 'info', title, message, duration }) {
    // Set default duration based on type
    if (!duration) {
      switch (type) {
        case 'success': duration = 5000; break;
        case 'error': duration = 8000; break;
        case 'warning': duration = 6000; break;
        case 'info': default: duration = 5000;
      }
    }
    
    const hasTitle = title !== undefined && title !== null && `${title}`.trim().length > 0;
    const hasMessage = message !== undefined && message !== null && `${message}`.trim().length > 0;

    // Set icon based on type
    let icon = '💬'; // Default info icon
    switch (type) {
      case 'success': icon = '✅'; break;
      case 'error': icon = '❌'; break;
      case 'warning': icon = '⚠️'; break;
      default: icon = '💬'; break;
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `battleroom-notification ${type}`;
    notification.innerHTML = `
      <span class="icon">${icon}</span>
      <div class="content">
        ${hasTitle ? `<h4>${title}</h4>` : ''}
        ${hasMessage ? `<p>${message}</p>` : ''}
      </div>
      <span class="close">×</span>
    `;
    
    // Add to DOM
    document.body.appendChild(notification);
    
    // Add event listener for the close button
    const closeButton = notification.querySelector('.close');
    closeButton.addEventListener('click', () => {
      this.close(notification);
    });
    
    // Auto-remove after specified duration
    setTimeout(() => {
      this.close(notification);
    }, duration);
    
    return notification;
  }
  
  /**
   * Show a success notification
   * @param {string} title - Title of the notification
   * @param {string} message - Message content
   * @param {number} duration - Optional duration override
   * @returns {HTMLElement} The created notification element
   */
  static success(title, message, duration) {
    return this.show({ type: 'success', title, message, duration });
  }
  
  /**
   * Show an error notification
   * @param {string} title - Title of the notification 
   * @param {string} message - Message content
   * @param {number} duration - Optional duration override
   * @returns {HTMLElement} The created notification element
   */
  static error(title, message, duration) {
    return this.show({ type: 'error', title, message, duration });
  }
  
  /**
   * Show a warning notification
   * @param {string} title - Title of the notification
   * @param {string} message - Message content
   * @param {number} duration - Optional duration override
   * @returns {HTMLElement} The created notification element
   */
  static warning(title, message, duration) {
    return this.show({ type: 'warning', title, message, duration });
  }
  
  /**
   * Show an info notification
   * @param {string} title - Title of the notification
   * @param {string} message - Message content
   * @param {number} duration - Optional duration override
   * @returns {HTMLElement} The created notification element
   */
  static info(title, message, duration) {
    return this.show({ type: 'info', title, message, duration });
  }
  
  /**
   * Close a notification with animation
   * @param {HTMLElement} notification - The notification element to close
   */
  static close(notification) {
    if (!notification || !document.body.contains(notification)) {
      return;
    }
    
    // Add animation class
    notification.style.animation = 'slide-out 0.3s forwards';
    
    // Remove from DOM after animation completes
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 300); // Match animation duration
  }
  
  /**
   * Close all active notifications
   */
  static closeAll() {
    const notifications = document.querySelectorAll('.battleroom-notification');
    notifications.forEach(notification => {
      this.close(notification);
    });
  }
}

export default NotificationManager;
